import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import AdminNav from "../../components/AdminNav";
import api from "../../utils/api";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "starred", label: "Starred" },
];

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return isToday
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminInbox() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, starred: 0 });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | 'single' | 'bulk'

  const searchTimeout = useRef(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchEmails = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/inbox", {
          params: { page, limit: 25, filter, search: search || undefined },
        });
        setEmails(data.emails);
        setPagination(data.pagination);
        setSelected(new Set());
      } catch (error) {
        console.error("Error fetching inbox:", error);
        toast.error("Failed to load inbox");
      } finally {
        setLoading(false);
      }
    },
    [filter, search],
  );

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/inbox/stats");
      setStats(data.stats);
    } catch (error) {
      console.error("Error fetching inbox stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchEmails(1);
    fetchStats();
  }, [fetchEmails, fetchStats]);

  // -------------------------------------------------------------------------
  // Search with debounce
  // -------------------------------------------------------------------------

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(value);
    }, 350);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
  };

  // -------------------------------------------------------------------------
  // Open / read email
  // -------------------------------------------------------------------------

  const openEmail = async (email) => {
    setEmailLoading(true);
    setSelectedEmail(null);
    try {
      const { data } = await api.get(`/admin/inbox/${email.id}`);
      setSelectedEmail(data.email);
      // Update list item to reflect read state
      setEmails((prev) =>
        prev.map((e) => (e.id === email.id ? { ...e, is_read: true } : e)),
      );
      fetchStats();
    } catch (error) {
      console.error("Error opening email:", error);
      toast.error("Failed to load email");
    } finally {
      setEmailLoading(false);
    }
  };

  const closeEmail = () => setSelectedEmail(null);

  // -------------------------------------------------------------------------
  // Star / unstar
  // -------------------------------------------------------------------------

  const toggleStar = async (email, e) => {
    e.stopPropagation();
    const newValue = !email.is_starred;
    // Optimistic update
    setEmails((prev) =>
      prev.map((em) => (em.id === email.id ? { ...em, is_starred: newValue } : em)),
    );
    if (selectedEmail?.id === email.id) {
      setSelectedEmail((prev) => ({ ...prev, is_starred: newValue }));
    }
    try {
      await api.patch(`/admin/inbox/${email.id}/starred`, { is_starred: newValue });
      fetchStats();
    } catch (error) {
      console.error("Error toggling star:", error);
      toast.error("Failed to update star");
      // Revert
      setEmails((prev) =>
        prev.map((em) => (em.id === email.id ? { ...em, is_starred: !newValue } : em)),
      );
    }
  };

  // -------------------------------------------------------------------------
  // Mark read / unread
  // -------------------------------------------------------------------------

  const toggleRead = async (email, e) => {
    e.stopPropagation();
    const newValue = !email.is_read;
    setEmails((prev) =>
      prev.map((em) => (em.id === email.id ? { ...em, is_read: newValue } : em)),
    );
    try {
      await api.patch(`/admin/inbox/${email.id}/read`, { is_read: newValue });
      fetchStats();
    } catch (error) {
      console.error("Error toggling read:", error);
      toast.error("Failed to update read status");
      setEmails((prev) =>
        prev.map((em) => (em.id === email.id ? { ...em, is_read: !newValue } : em)),
      );
    }
  };

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === emails.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(emails.map((e) => e.id)));
    }
  };

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------

  const confirmDelete = (type) => setDeleteConfirm(type);
  const cancelDelete = () => setDeleteConfirm(null);

  const deleteSelected = async () => {
    setDeleteConfirm(null);
    const ids = Array.from(selected);
    try {
      await api.delete("/admin/inbox", { data: { ids } });
      toast.success(`${ids.length} email(s) deleted`);
      if (selectedEmail && ids.includes(selectedEmail.id)) {
        setSelectedEmail(null);
      }
      fetchEmails(pagination.page);
      fetchStats();
    } catch (error) {
      console.error("Error deleting emails:", error);
      toast.error("Failed to delete emails");
    }
  };

  const deleteSingle = async (id) => {
    setDeleteConfirm(null);
    try {
      await api.delete(`/admin/inbox/${id}`);
      toast.success("Email deleted");
      if (selectedEmail?.id === id) setSelectedEmail(null);
      fetchEmails(pagination.page);
      fetchStats();
    } catch (error) {
      console.error("Error deleting email:", error);
      toast.error("Failed to delete email");
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const unreadCount = parseInt(stats.unread, 10) || 0;
  const allSelected = emails.length > 0 && selected.size === emails.length;
  const someSelected = selected.size > 0 && !allSelected;

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 pb-12">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Inbox
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-medium bg-gold-500 text-black rounded-full px-2 py-0.5">
                  {unreadCount}
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Inbound mail received at admin@tabrams.com
            </p>
          </div>

          {/* Stats pills */}
          <div className="hidden sm:flex gap-3 text-sm">
            <span className="bg-gray-800 text-gray-300 rounded-full px-3 py-1">
              {stats.total ?? 0} total
            </span>
            <span className="bg-gray-800 text-gray-300 rounded-full px-3 py-1">
              {stats.unread ?? 0} unread
            </span>
            <span className="bg-gray-800 text-gray-300 rounded-full px-3 py-1">
              {stats.starred ?? 0} starred
            </span>
          </div>
        </div>

        <div className="flex gap-6">
          {/* ----------------------------------------------------------------
              Left panel — email list
          ---------------------------------------------------------------- */}
          <div className={`flex-1 min-w-0 ${selectedEmail ? "hidden lg:block lg:w-2/5 lg:flex-none" : ""}`}>

            {/* Toolbar */}
            <div className="bg-gray-900 border border-gray-800 rounded-t-lg px-4 py-3 flex flex-wrap gap-3 items-center">
              {/* Select all checkbox */}
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected)}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-gold-500 cursor-pointer"
                title="Select all"
              />

              {/* Filters */}
              <div className="flex gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFilter(f.value)}
                    className={`px-3 py-1 text-xs rounded font-medium transition-colors ${
                      filter === f.value
                        ? "bg-gold-500 text-black"
                        : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="flex-1 min-w-[140px] relative">
                <input
                  type="text"
                  placeholder="Search…"
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500"
                />
                {searchInput && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Bulk delete */}
              {selected.size > 0 && (
                <button
                  onClick={() => confirmDelete("bulk")}
                  className="ml-auto text-xs text-red-400 hover:text-red-300 font-medium"
                >
                  Delete ({selected.size})
                </button>
              )}
            </div>

            {/* Email rows */}
            <div className="border border-t-0 border-gray-800 rounded-b-lg overflow-hidden divide-y divide-gray-800">
              {loading ? (
                <div className="py-16 text-center text-gray-500">Loading…</div>
              ) : emails.length === 0 ? (
                <div className="py-16 text-center text-gray-500">
                  {search ? "No emails match your search" : "No emails yet"}
                </div>
              ) : (
                emails.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => openEmail(email)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      selectedEmail?.id === email.id
                        ? "bg-gray-800"
                        : email.is_read
                        ? "bg-gray-950 hover:bg-gray-900"
                        : "bg-black hover:bg-gray-900"
                    }`}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selected.has(email.id)}
                      onChange={(e) => toggleSelect(email.id, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 accent-gold-500 cursor-pointer flex-shrink-0"
                    />

                    {/* Star */}
                    <button
                      onClick={(e) => toggleStar(email, e)}
                      className={`mt-0.5 flex-shrink-0 text-base leading-none ${
                        email.is_starred ? "text-gold-400" : "text-gray-600 hover:text-gray-400"
                      }`}
                      title={email.is_starred ? "Unstar" : "Star"}
                    >
                      ★
                    </button>

                    {/* Unread dot */}
                    <div className="mt-2 flex-shrink-0">
                      <div
                        className={`w-2 h-2 rounded-full ${email.is_read ? "bg-transparent" : "bg-gold-500"}`}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={`text-sm truncate ${email.is_read ? "text-gray-400" : "text-white font-semibold"}`}>
                          {email.from_name || email.from_email}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(email.received_at)}</span>
                      </div>
                      <div className={`text-sm truncate ${email.is_read ? "text-gray-500" : "text-gray-200"}`}>
                        {email.subject}
                      </div>
                      <div className="text-xs text-gray-600 truncate mt-0.5">{email.preview}</div>
                    </div>

                    {/* Attachments badge */}
                    {email.attachments_count > 0 && (
                      <span className="mt-1 flex-shrink-0 text-xs text-gray-500" title="Has attachments">
                        📎 {email.attachments_count}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => fetchEmails(p)}
                    className={`w-8 h-8 text-sm rounded ${
                      p === pagination.page
                        ? "bg-gold-500 text-black font-bold"
                        : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ----------------------------------------------------------------
              Right panel — email detail
          ---------------------------------------------------------------- */}
          {selectedEmail && (
            <div className="flex-1 min-w-0 bg-gray-900 border border-gray-800 rounded-lg flex flex-col">
              {/* Detail toolbar */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-800">
                <button
                  onClick={closeEmail}
                  className="lg:hidden text-gray-400 hover:text-white text-sm"
                >
                  ← Back
                </button>

                {/* Star */}
                <button
                  onClick={(e) => toggleStar(selectedEmail, e)}
                  className={`text-base ${selectedEmail.is_starred ? "text-gold-400" : "text-gray-500 hover:text-gray-300"}`}
                  title={selectedEmail.is_starred ? "Unstar" : "Star"}
                >
                  ★
                </button>

                {/* Mark read/unread */}
                <button
                  onClick={(e) => toggleRead(selectedEmail, e)}
                  className="text-xs text-gray-400 hover:text-white border border-gray-700 rounded px-2 py-0.5"
                >
                  {selectedEmail.is_read ? "Mark unread" : "Mark read"}
                </button>

                <div className="flex-1" />

                {/* Delete */}
                <button
                  onClick={() => confirmDelete("single")}
                  className="text-xs text-red-400 hover:text-red-300 border border-red-900 rounded px-2 py-0.5"
                >
                  Delete
                </button>
              </div>

              {/* Email detail */}
              {emailLoading ? (
                <div className="py-24 text-center text-gray-500">Loading…</div>
              ) : (
                <div className="flex-1 overflow-auto p-5">
                  {/* Subject */}
                  <h2 className="text-lg font-semibold text-white mb-3">
                    {selectedEmail.subject || "(no subject)"}
                  </h2>

                  {/* Meta */}
                  <div className="text-sm text-gray-400 space-y-1 mb-5 pb-5 border-b border-gray-800">
                    <div>
                      <span className="text-gray-600">From: </span>
                      {selectedEmail.from_name ? (
                        <>
                          <span className="text-white">{selectedEmail.from_name}</span>
                          <span className="text-gray-500"> &lt;{selectedEmail.from_email}&gt;</span>
                        </>
                      ) : (
                        <span className="text-white">{selectedEmail.from_email}</span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-600">To: </span>
                      <span className="text-white">{selectedEmail.to_email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date: </span>
                      <span className="text-white">
                        {new Date(selectedEmail.received_at).toLocaleString()}
                      </span>
                    </div>
                    {selectedEmail.attachments_count > 0 && (
                      <div>
                        <span className="text-gray-600">Attachments: </span>
                        <span className="text-white">{selectedEmail.attachments_count}</span>
                      </div>
                    )}
                    {selectedEmail.spam_score !== null && selectedEmail.spam_score !== undefined && (
                      <div>
                        <span className="text-gray-600">Spam score: </span>
                        <span className={selectedEmail.spam_score > 5 ? "text-red-400" : "text-white"}>
                          {selectedEmail.spam_score}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  {selectedEmail.html_body ? (
                    <div
                      className="prose prose-invert prose-sm max-w-none bg-white text-black rounded p-4 text-sm"
                      dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans leading-relaxed">
                      {selectedEmail.text_body || "(empty body)"}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------------------
          Delete confirmation modal
      ---------------------------------------------------------------- */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Confirm delete</h3>
            <p className="text-gray-400 text-sm mb-5">
              {deleteConfirm === "bulk"
                ? `Delete ${selected.size} selected email(s)? This cannot be undone.`
                : "Delete this email? This cannot be undone."}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  deleteConfirm === "bulk"
                    ? deleteSelected()
                    : deleteSingle(selectedEmail?.id)
                }
                className="px-4 py-2 text-sm text-white bg-red-700 hover:bg-red-600 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
